import { Link } from "react-router-dom";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { useSiteKeyActivity } from "./siteKeysApi";
import { KEYS_PAGE_SIZE_OPTIONS, useKeysSearchParams } from "./useKeysSearchParams";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** The fob detail's Activity sub-tab: where the fob was used, from the locks' activity. */
export default function SiteKeyActivityTable({
  siteId,
  credential,
}: {
  siteId: string;
  credential: string;
}) {
  const { page, pageSize, setPage, setPageSize } = useKeysSearchParams();
  const { data, isLoading, isError, isFetching, isPlaceholderData } = useSiteKeyActivity(
    siteId,
    credential,
    page,
    pageSize,
  );
  const activities = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <AdminTable
      aria-label="Fob activity"
      aria-busy={isFetching}
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          pageSizeOptions={KEYS_PAGE_SIZE_OPTIONS}
          visibleRowCount={activities.length}
          // Also while the next page loads (the previous page stays on screen meanwhile).
          isLoading={isLoading || isPlaceholderData}
          onNext={() => setPage(page + 1)}
          onPrevious={() => setPage(Math.max(0, page - 1))}
          onPageSizeChange={setPageSize}
          disablePrevious={page === 0}
          disableNext={totalCount <= (page + 1) * pageSize}
        />
      }
    >
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Lock</th>
          <th scope="col">Category</th>
          <th scope="col">Details</th>
        </tr>
      </thead>
      <tbody>
        {isError ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-rose-700">
              Error loading activity.
            </td>
          </tr>
        ) : isLoading ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              Loading activity...
            </td>
          </tr>
        ) : activities.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              No activity found for this fob.
            </td>
          </tr>
        ) : (
          activities.map((activity, index) => (
            <tr key={`${activity.timestamp}-${activity.lockId}-${index}`}>
              <td className="whitespace-nowrap">
                {new Date(activity.timestamp).toLocaleString()}
              </td>
              <td>
                {activity.lockId && activity.lockId !== EMPTY_GUID ? (
                  <Link to={`/admin/locks/${activity.lockId}`}>
                    {activity.lockName || activity.lockId}
                  </Link>
                ) : (
                  activity.lockName || <span className="text-slate-400">—</span>
                )}
              </td>
              <td>{activity.lockActivityCategory}</td>
              <td>{activity.details || <span className="text-slate-400">—</span>}</td>
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}
